import PropTypes from 'prop-types';
import {createRef, Component, RefObject} from 'react';

import * as MultiSelectListUtil from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {
  MetadataForRestore,
  MultiExtra,
  MultiSelectSingleParam,
  MultiSelectState,
} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ElementClipboardEvent, ElementKeyboardEvent, ElementMouseEvent} from 'util/Types';

type RequiredMethod =
  | 'previousIndex'
  | 'nextIndex'
  | 'firstIndex'
  | 'lastIndex'
  | 'compareIndex'
  | 'multiSelectKey'
  | 'isIndexBetween'
  | 'multiKeyDownHandler'
  | 'multiInputHandler'
  | 'multiPasteHandler'
  | 'multiGetHTMLForCopy'
  | 'isLockedWithEditWarn'
  | '_getRoot';

// Defines the component type that uses this mixin
export interface MultiSelectListComponent<
  TIndex,
  P extends MultiSelectListProps<TIndex> = MultiSelectListProps<TIndex>,
  S extends MultiSelectState<TIndex> = MultiSelectState<TIndex>,
> extends Component<P, S> {
  // Methods and fields defined on this mixin itself
  _multiWindowMouseDownHandler: (e: MouseEvent) => void;
  _multiWindowMouseUpHandler: (e: MouseEvent) => void;
  focusBearerRef: RefObject<HTMLElement>;
  setMultiIndices: (args: {start: TIndex; end: TIndex; text?: string}) => void;
  sortedMultiRange: () => {start: TIndex; end: TIndex} | null;
  // Methods and fields that must be provided by consumer
  previousIndex: (index: TIndex) => TIndex;
  nextIndex: (index: TIndex) => TIndex;
  firstIndex: () => TIndex;
  lastIndex: () => TIndex;
  compareIndex: (a: TIndex, b: TIndex) => number;
  multiSelectKey: () => string;
  isIndexBetween: (first: TIndex, second: TIndex, indexToCheck: TIndex) => boolean;
  multiKeyDownHandler: (e: KeyboardEvent) => void;
  multiInputHandler: (args: {index?: TIndex; text: string}) => void;
  multiPasteHandler: (e: ElementClipboardEvent) => void;
  multiGetHTMLForCopy: (args?: {showFormulas?: boolean}) => string;
  isLockedWithEditWarn: () => boolean;
  _getRoot: () => Element;
  // Methods and fields that the constructor doesn't SAY must be provided by consumer, but which nevertheless
  // must
  allowSingleMultiSelect: () => boolean;
  // Methods and fields that may be provided by consumer
  getMultiEditingText?: (index: TIndex) => string;
  getMultiEditingTypedValue?: (index: TIndex) => TypedValue | null;
  improperFocusHook?: () => void;
  multiCanInsert?: (args: {start: number; end: number; cid: string; shift: boolean}) => boolean;
  multiCrossEditing?: () => boolean;
  multiFinalizeEditHandler?: () => void;
  multiGetTextForCopy?: () => string;
  multiNamespace?: () => string;
  multiTryInsert?: (args: {
    index: TIndex;
    cid: string;
    start: number;
    end: number;
    shift: boolean;
  }) => boolean;
  setMultiEditing?: (args: {index: TIndex | null; editing: boolean; text: string | null}) => void;
}

export interface MultiSelectListProps<TIndex> {
  disallowMultiExpand?: () => boolean;
  locked: boolean;
  metadataForRestore: MetadataForRestore<TIndex>;
  multiEditing: boolean;
  restoreState?: (
    e: ElementKeyboardEvent,
    selectionRestoreData: MetadataForRestore<TIndex> | {selection: SelectionRestoreData}
  ) => boolean;
  storeState?: (state: MetadataForRestore<TIndex>) => void;
}

export default {
  propTypes: {
    storeState: PropTypes.func,
    restoreState: PropTypes.func,
    metadataForRestore: PropTypes.object,
    multiEditing: PropTypes.bool, // specifies if the component is being multi-edited
  },

  getInitialState<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>
  ): MultiSelectState<TIndex> {
    return {
      multiStartIndex: null, // anchor index / index selection started on
      multiEndIndex: null, // index selection ended on
      multiActive: null, // null if selection is not being updated
      multiSelIndex: null, // currently selected index within the selection
      multiActualIndex: null, // stateful index to keep track during navigation (could be in a merged cell)
      multiExtra: {}, // any state that should be tied to the selection's existence
    };
  },

  UNSAFE_componentWillMount<
    TIndex,
    P extends MultiSelectListProps<TIndex>,
    S extends MultiSelectState<TIndex>,
  >(this: MultiSelectListComponent<TIndex, P, S>): void {
    const requiredMethods: Array<RequiredMethod> = [
      'previousIndex',
      'nextIndex',
      'firstIndex',
      'lastIndex',
      'compareIndex',
      'multiSelectKey',
      'isIndexBetween',
      'multiKeyDownHandler',
      'multiInputHandler',
      'multiPasteHandler',
      'multiGetHTMLForCopy',
      'isLockedWithEditWarn',
      '_getRoot',
    ];

    // A component may also implement:
    //   multiGetTextForCopy: Returns text data to put on the clipboard.
    //   multiFinalizeEditHandler: A hook to handle when an item is being deselected/blurred.
    //   multiCrossEditing(): Returns true if we are actively updating a different multiselect.
    //     and that this component should multiTryInsert
    //   multiTryInsert({index, cid, cursor, shift}):
    //     Attempts to insert a cell reference at index into the current formula at position
    //     cursor with shift to indicate if it should expand/set the range. Returns true,
    //     if the insert was successful.
    //   multiCanInsert({start, end, cid, shift}):
    //     Determines if multiTryInsert would return true without making any modifications
    //   setMultiEditing({editing: bool, text: string}):
    //     Set the non-finalized text for the item that is being multi-edited
    //       (item with index @state.multiSelIndex when @props.multiEditing).
    //     Pass {editing: false} to exit multiEditing mode.
    //   multiNamespace: the data-namespace on elements that informs window mousedown to allow a cross-insert
    for (const method of requiredMethods) {
      if (this[method] == null) {
        // @ts-expect-error `displayName` is not represented in mixin types but should in fact exist
        throw new Error(`${this.constructor.displayName} must implement ${method}.`);
      }
    }
    MultiSelectListUtil.multiSelectableWillMount(
      this.props.metadataForRestore,
      this.multiSelectKey(),
      this.setState.bind(this)
    );
    this.focusBearerRef = createRef();
  },

  componentDidMount<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>
  ): void {
    MultiSelectListUtil.multiSelectableDidMount(
      this._multiWindowMouseDownHandler,
      this._multiWindowMouseUpHandler
    );
  },

  componentWillUnmount<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>
  ): void {
    MultiSelectListUtil.multiSelectableWillUnmount(
      this._multiWindowMouseDownHandler,
      this._multiWindowMouseUpHandler
    );
  },

  _multiWindowMouseDownHandler<
    TIndex,
    P extends MultiSelectListProps<TIndex>,
    S extends MultiSelectState<TIndex>,
  >(this: MultiSelectListComponent<TIndex, P, S>, e: MouseEvent): void {
    const options = {
      multiEditing: this.props.multiEditing,
      multiCanInsert: this.multiCanInsert,
      multiNamespace: this.multiNamespace ? this.multiNamespace() : undefined,
      multiFinalizeEditHandler: this.multiFinalizeEditHandler,
      domNode: this._getRoot(),
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      setMultiEditing: this.setMultiEditing,
      setState: this.setState.bind(this),
      multiActive: this.state.multiActive,
      multiCrossEditing: this.multiCrossEditing ? this.multiCrossEditing() : false,
    };
    MultiSelectListUtil.multiWindowMouseDownHandler(e, options);
  },

  _multiWindowMouseUpHandler<
    TIndex,
    P extends MultiSelectListProps<TIndex>,
    S extends MultiSelectState<TIndex>,
  >(this: MultiSelectListComponent<TIndex, P, S>, e: MouseEvent): void {
    const options = {
      multiEditing: this.props.multiEditing,
      multiCanInsert: this.multiCanInsert,
      multiNamespace: this.multiNamespace ? this.multiNamespace() : undefined,
      multiFinalizeEditHandler: this.multiFinalizeEditHandler,
      domNode: this._getRoot(),
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      setMultiEditing: this.setMultiEditing,
      setState: this.setState.bind(this),
      multiActive: this.state.multiActive,
      multiCrossEditing: this.multiCrossEditing ? this.multiCrossEditing() : false,
    };
    MultiSelectListUtil.multiWindowMouseUpHandler(e, options);
  },

  UNSAFE_componentWillReceiveProps<
    TIndex,
    P extends MultiSelectListProps<TIndex>,
    S extends MultiSelectState<TIndex>,
  >(this: MultiSelectListComponent<TIndex, P, S>, nextProps: MultiSelectListProps<TIndex>): void {
    MultiSelectListUtil.multiSelectableWillReceiveProps(
      this.props,
      nextProps,
      this.multiSelectKey(),
      this.setState.bind(this)
    );
  },

  componentDidUpdate<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>
  ): void {
    const options = {
      domNode: this._getRoot(),
      multiEditing: this.props.multiEditing,
      improperFocusHook: this.improperFocusHook,
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      focusBearerNode: this.focusBearerRef.current!,
    };
    MultiSelectListUtil.multiSelectableDidUpdate(options);
  },

  // # Component-callable helper methods

  sortedMultiRange<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>
  ): {start: TIndex; end: TIndex} | null {
    return MultiSelectListUtil.sortedMultiRange<TIndex>(
      this.state.multiStartIndex,
      this.state.multiEndIndex,
      this.compareIndex
    );
  },

  // Takes the index of the note part with the current selection (could be null if multi-select).
  // If there is a multi-select, returns the multi-range.
  // Otherwise, returns a range containing just the passed in index.
  getSelectedRange<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>,
    index: TIndex
  ): {start: TIndex; end: TIndex} | null {
    return MultiSelectListUtil.getSelectedRange<TIndex>(index, {
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      compareIndex: this.compareIndex,
    });
  },

  saveMultiSelect<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>,
    state: MultiSelectState<TIndex>
  ): void {
    if (state == null) {
      ({state} = this);
    }
    MultiSelectListUtil.saveMultiSelect(state, this.props.storeState, this.multiSelectKey());
  },

  clearMultiSelect<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>
  ): void {
    MultiSelectListUtil.clearMultiSelect(this.setState.bind(this));
  },

  setMultiExtra<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>,
    multiExtraUpdate: Partial<MultiExtra>
  ): void {
    MultiSelectListUtil.setMultiExtra(multiExtraUpdate, this.setState.bind(this), this.state.multiExtra);
  },

  multiSelectSingle<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>,
    index: TIndex,
    param?: MultiSelectSingleParam<TIndex>
  ): void {
    const options = {
      setState: this.setState.bind(this),
      multiStartIndex: this.state.multiStartIndex as TIndex,
      multiEndIndex: this.state.multiEndIndex as TIndex,
      setMultiEditing: this.setMultiEditing,
      getMultiEditingText: this.getMultiEditingText!,
      getMultiEditingTypedValue: this.getMultiEditingTypedValue!,
      focusBearerNode: this.focusBearerRef.current!,
    };
    MultiSelectListUtil.multiSelectSingle(index, param, options);
  },

  multiCancelEdit<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>,
    index: TIndex
  ): void {
    const options = {
      setMultiEditing: this.setMultiEditing,
      getMultiEditingText: this.getMultiEditingText!,
      getMultiEditingTypedValue: this.getMultiEditingTypedValue!,
      focusBearerNode: this.focusBearerRef.current!,
    };
    MultiSelectListUtil.multiCancelEdit(index, options);
  },

  setMultiIndices<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>,
    {start, end, text}: {start: TIndex; end: TIndex; text?: string}
  ): void {
    const options = {
      setState: this.setState.bind(this),
      setMultiEditing: this.setMultiEditing,
      getMultiEditingText: this.getMultiEditingText!,
      getMultiEditingTypedValue: this.getMultiEditingTypedValue!,
      focusBearerNode: this.focusBearerRef.current!,
    };
    MultiSelectListUtil.setMultiIndices({start, end, text}, options);
  },

  isSelected<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>,
    index: TIndex
  ): boolean {
    return MultiSelectListUtil.isSelected<TIndex>(
      index,
      this.state.multiStartIndex,
      this.state.multiEndIndex,
      this.isIndexBetween
    );
  },

  hasMultiSelection<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>
  ): boolean {
    return MultiSelectListUtil.hasMultiSelection(this.state.multiStartIndex, this.state.multiEndIndex);
  },

  // # Component-registered handlers to manage multiselect state.

  multiEditableMouseDownHandler<
    TIndex,
    P extends MultiSelectListProps<TIndex>,
    S extends MultiSelectState<TIndex>,
  >(
    this: MultiSelectListComponent<TIndex, P, S>,
    args: {
      start: TIndex;
      end?: TIndex;
      active?: boolean | string;
      allowSingleOverride?: boolean;
      isMultiExpandBuffered?: boolean;
    },
    e: ElementMouseEvent
  ): void {
    const options = {
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      multiSelIndex: this.state.multiSelIndex,
      setState: this.setState.bind(this),
      allowSingleMultiSelect: this.allowSingleMultiSelect(),
      multiTryInsert: this.multiTryInsert!,
      multiCrossEditing: this.multiCrossEditing ? this.multiCrossEditing() : false,
      multiFinalizeEditHandler: this.multiFinalizeEditHandler,
      isIndexBetween: this.isIndexBetween,
      compareIndex: this.compareIndex,
      setMultiEditing: this.setMultiEditing,
      getMultiEditingText: this.getMultiEditingText!,
      getMultiEditingTypedValue: this.getMultiEditingTypedValue!,
      focusBearerNode: this.focusBearerRef.current!,
      disallowShiftExpand: false,
      multiGetHTMLForCopy: this.multiGetHTMLForCopy,
      multiGetTextForCopy: this.multiGetTextForCopy,
    };
    MultiSelectListUtil.multiEditableMouseDownHandler<TIndex>(args, e, options);
  },

  multiEditableMouseOverHandler<
    TIndex,
    P extends MultiSelectListProps<TIndex>,
    S extends MultiSelectState<TIndex>,
  >(this: MultiSelectListComponent<TIndex, P, S>, index: TIndex): void {
    const options = {
      setState: this.setState.bind(this),
      multiActive: this.state.multiActive,
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      multiExtra: this.state.multiExtra,
      disallowMultiExpand: this.props.disallowMultiExpand ? this.props.disallowMultiExpand() : false,
      multiCrossEditing: this.multiCrossEditing ? this.multiCrossEditing() : false,
      allowSingleMultiSelect: this.allowSingleMultiSelect(),
      multiTryInsert: this.multiTryInsert!,
      compareIndex: this.compareIndex,
      focusBearerNode: this.focusBearerRef.current!,
    };
    MultiSelectListUtil.multiEditableMouseOverHandler(index, options);
  },

  multiEditableMouseMoveHandler<
    TIndex,
    P extends MultiSelectListProps<TIndex>,
    S extends MultiSelectState<TIndex>,
  >(this: MultiSelectListComponent<TIndex, P, S>, index: TIndex, e: MouseEvent): void {
    const options = {
      setState: this.setState.bind(this),
      multiActive: this.state.multiActive,
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      multiExtra: this.state.multiExtra,
      compareIndex: this.compareIndex,
    };
    MultiSelectListUtil.multiEditableMouseMoveHandler(index, e, options);
  },

  computeState<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>,
    state: Partial<MultiSelectState<TIndex>>
  ): MetadataForRestore<TIndex> {
    return MultiSelectListUtil.computeState(state, this.multiSelectKey());
  },

  renderFocusBearer<TIndex, P extends MultiSelectListProps<TIndex>, S extends MultiSelectState<TIndex>>(
    this: MultiSelectListComponent<TIndex, P, S>
  ): JSX.Element {
    const options = {
      state: this.state,
      isLockedWithEditWarn: () => this.isLockedWithEditWarn(),
      restoreState: this.props.restoreState,
      setState: (state: S) => {
        this.setState(state);
      },
      setMultiEditing: this.setMultiEditing,
      getMultiEditingText: this.getMultiEditingText,
      getMultiEditingTypedValue: this.getMultiEditingTypedValue,
      firstIndex: this.firstIndex(),
      lastIndex: this.lastIndex(),
      multiSelectable: this,
      multiSelectKey: this.multiSelectKey(),
      multiSelIndex: this.state.multiSelIndex,
      multiKeyDownHandler: this.multiKeyDownHandler,
      multiInputHandler: this.multiInputHandler,
      multiPasteHandler: this.multiPasteHandler,
      multiGetHTMLForCopy: this.multiGetHTMLForCopy,
      multiGetTextForCopy: this.multiGetTextForCopy,
      ref: this.focusBearerRef,
    };
    // @ts-expect-error MultiSelectListUtil expects `ref` to be HTMLElement ref rather than HTMLDivElement
    return MultiSelectListUtil.renderFocusBearer(options);
  },
};
